import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  getDocs,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../config/firebase";

const USERS_COLLECTION = "users";

// Role hierarchy: super-admin > admin > user
export const ROLES = {
  SUPER_ADMIN: "super-admin",
  ADMIN: "admin",
  USER: "user",
};

export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 3,
  [ROLES.ADMIN]: 2,
  [ROLES.USER]: 1,
};

/**
 * Check if a role has sufficient privileges
 */
export function hasRole(userRole, requiredRole) {
  return (ROLE_HIERARCHY[userRole] || 0) >= (ROLE_HIERARCHY[requiredRole] || 0);
}

/**
 * Get a user profile by UID
 */
export async function getUserProfile(uid) {
  const docRef = doc(db, USERS_COLLECTION, uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { uid, ...docSnap.data() };
  }
  return null;
}

/**
 * Create or update a user profile
 */
export async function createUserProfile(uid, data) {
  const docRef = doc(db, USERS_COLLECTION, uid);
  const existing = await getDoc(docRef);

  if (existing.exists()) {
    // Update existing profile
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } else {
    // Create new profile
    await setDoc(docRef, {
      email: data.email,
      displayName: data.displayName || null,
      role: data.role || ROLES.USER,
      status: data.status || "active",
      invitedBy: data.invitedBy || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  return getUserProfile(uid);
}

/**
 * Update a user's role (requires super-admin privileges)
 * Uses Cloud Function to ensure proper authorization
 */
export async function updateUserRole(uid, newRole) {
  const updateRole = httpsCallable(functions, "updateUserRole");
  const result = await updateRole({ uid, role: newRole });
  return result.data.user;
}

/**
 * Update user status - enable/disable in Firebase Auth
 * Uses Cloud Function to update both Auth and Firestore
 */
export async function updateUserStatus(uid, status) {
  const setDisabled = httpsCallable(functions, "setUserDisabled");
  const disabled = status === "suspended" || status === "disabled";
  const result = await setDisabled({ uid, disabled });
  return result.data.user;
}

/**
 * List all users from Firebase Auth merged with Firestore profiles
 * Uses Cloud Function to access Admin SDK
 */
export async function listUsers() {
  const listAllUsers = httpsCallable(functions, "listAllUsers");
  const result = await listAllUsers();
  return result.data.users;
}

/**
 * Create a Firestore profile for an existing Auth user
 * Uses Cloud Function to ensure proper authorization
 */
export async function createUserProfileViaFunction(uid, data) {
  const createProfile = httpsCallable(functions, "createUserProfile");
  const result = await createProfile({ uid, ...data });
  return result.data.user;
}

/**
 * Check if a user profile exists
 */
export async function userProfileExists(uid) {
  const docRef = doc(db, USERS_COLLECTION, uid);
  const docSnap = await getDoc(docRef);
  return docSnap.exists();
}

/**
 * Get user by email
 */
export async function getUserByEmail(email) {
  const q = query(
    collection(db, USERS_COLLECTION),
    where("email", "==", email)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { uid: doc.id, ...doc.data() };
}
